import { Link, useParams } from 'react-router-dom'
import { Heart, ListPlus, Package } from 'lucide-react'

import { useSaveToList } from './saveToListContext'
import { useFavouriteToggle } from './useFavouriteToggle'
import { ProductViewSource } from '../../../lib/analytics'
import type { FavouriteTarget } from '../../../auth/hooks/useFavourites'
import type { Favourite } from '../../../types'

const ICON_BUTTON =
  'flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl transition-colors hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary'

/**
 * One favourite in a compact card list (All favourites and list screens,
 * DEV-51010): 44px thumbnail, name with the price under it, then two 44px
 * buttons — "Add to list" (icon with a small "List" caption) and the heart.
 *
 * The price sits under the name in dark text: brand-coloured prices failed
 * contrast (2.9:1) and moving it frees room for the two buttons. Supplier
 * name stays hidden — engineers identify a saved part by its part number.
 */
export function FavouriteRow({ favourite }: { favourite: Favourite }) {
  const { tenantSlug } = useParams<{ tenantSlug: string }>()
  const { openSaveToList } = useSaveToList()
  const toggleFavourite = useFavouriteToggle()

  const listing = favourite.listing
  if (!listing) return null

  const productName = listing.displayName ?? 'Unnamed product'
  const target: FavouriteTarget | null =
    favourite.targetType === 'raw' && favourite.rawProductId != null
      ? { type: 'raw', id: favourite.rawProductId }
      : favourite.masterProductId != null
        ? { type: 'master', id: favourite.masterProductId }
        : null
  const detailPath =
    target?.type === 'raw'
      ? `/${tenantSlug}/engineer/supplier-product/${target.id}${listing.branchId ? `?branchId=${listing.branchId}` : ''}`
      : target
        ? `/${tenantSlug}/engineer/product/${target.id}`
        : null

  const summary = (
    <>
      <span className="flex h-11 w-11 flex-shrink-0 items-center justify-center overflow-hidden rounded-lg border border-gray-100 bg-gray-50">
        {listing.imageUrl ? (
          <img src={listing.imageUrl} alt="" loading="lazy" className="h-full w-full object-cover" />
        ) : (
          <Package className="h-5 w-5 text-gray-300" aria-hidden="true" />
        )}
      </span>
      <span className="min-w-0 flex-1">
        <span className="line-clamp-2 block break-words text-sm font-semibold leading-tight text-gray-900">
          {productName}
        </span>
        <span className="mt-1 flex min-w-0 items-baseline gap-1.5 text-xs">
          <span className="font-bold text-gray-900">
            {listing.price != null ? `£${listing.price.toFixed(2)}` : '—'}
          </span>
          {listing.mpn && (
            <span className="truncate font-medium text-gray-500">MPN: {listing.mpn}</span>
          )}
        </span>
      </span>
    </>
  )
  const summaryClass = 'flex min-h-11 min-w-0 flex-1 items-center gap-2.5 rounded-lg'

  return (
    <div className="flex items-center gap-2 py-[9px] pl-[15px] pr-[7px]">
      {detailPath ? (
        <Link
          to={detailPath}
          state={{ source: ProductViewSource.FAVOURITE }}
          className={`${summaryClass} focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary`}
        >
          {summary}
        </Link>
      ) : (
        <div className={summaryClass}>{summary}</div>
      )}

      <button
        type="button"
        onClick={() => openSaveToList({ favouriteId: favourite.id, productName })}
        className={`${ICON_BUTTON} flex-col gap-0.5 text-gray-600`}
        aria-label={`Add to list: ${productName}`}
      >
        <ListPlus className="h-5 w-5" aria-hidden="true" />
        {/* Caption like the bottom nav, so the icon isn't a guess. */}
        <span className="text-[10px] font-medium leading-none" aria-hidden="true">
          List
        </span>
      </button>
      {target && (
        <button
          type="button"
          data-favourite-heart
          onClick={(event) => {
            focusNeighbourHeart(event.currentTarget)
            toggleFavourite({ target, productName, favourited: true })
          }}
          className={`${ICON_BUTTON} -ml-2`}
          aria-label={`Remove from favourites: ${productName}`}
        >
          <Heart className="h-5 w-5 text-brand-primary" fill="currentColor" aria-hidden="true" />
        </button>
      )}
    </div>
  )
}

/**
 * Un-hearting removes the row, which would drop keyboard focus to the page.
 * Hand it to the next row's heart (or the previous one if this was the last).
 */
function focusNeighbourHeart(current: HTMLElement) {
  const hearts = Array.from(document.querySelectorAll<HTMLElement>('[data-favourite-heart]'))
  const index = hearts.indexOf(current)
  const neighbour = hearts[index + 1] ?? hearts[index - 1]
  requestAnimationFrame(() => {
    if (neighbour?.isConnected) neighbour.focus()
  })
}
